import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Dropdown, Form, FormSelect, Grid, Header, Icon, Menu, Modal, Popup, Select } from 'semantic-ui-react';
import { history } from '../../store/configureStore';
import { TextLink } from '../verizon/TextLink';
import { getSelectedLocation, redirectTo, roles, userAllowed, getMultiLocationOptions } from '../../helpers/utils';
import appLauncher from '../../semantic-ui/site/assets/icons/nav/app-launcher.svg';
import notification from '../../semantic-ui/site/assets/icons/nav/notification.svg';
import help from '../../semantic-ui/site/assets/icons/nav/question.svg';
import support from '../../semantic-ui/site/assets/icons/nav/support.svg';
import ticket from '../../semantic-ui/site/assets/icons/nav/add-ticket.svg';
import userProfile from '../../semantic-ui/site/assets/icons/nav/my-account.svg';
import './PageNav.less';
import {
    CREATE_TICKET_URL,
    THINGSPACE_URL,
    MY_BIZ_URL,
    EDGE_DEVELOPER_URL,
    FEEDBACK_FORM_URL,
    OSND_VIDEO_URL,
    OSND_VIDEO_PLAYLIST_URL,
    SOLUTION_TYPE
} from '../../helpers/const';
import ConfirmMessage from '../ConfirmMessage';
import NotificationComponent from '../notification/NotificationComponent';
import { getUserNotifications, getNewFeatureContent } from '../../actions/users';
import { updateNotificationCount } from '../../actions/notifications';
import { adminLogin } from '../../actions/auth';
import feedback from '../../semantic-ui/site/assets/feedback_blk.svg';
import { osndLocalStorage, storageConstant } from '../../helpers/localStorageUtils';
import { ENABLE_PENDO_FEEDBACK, ENABLE_VIDEO_GUIDE } from '../../helpers/featureFlags';
import {
    trackCreateTicketIconClicked,
    trackFeedbackFormIconClicked,
    trackSupportIconClicked,
    trackUserGuideMenuItemClicked,
    trackVideoTutorialsMenuItemClicked
} from '../../analytics/controlCenterAnalytics';
import newFeature from '../../semantic-ui/site/assets/newFeature.png';

const PageNav = (props) => {
    const dispatcher = useDispatch();
    const { location, setShowMobileSubNav, showEnterpriseSelection } = props;
    const {
        signedIn,
        roles: userRoles,
        enterpriseId,
        linkedEnterprises,
        isMultiSiteUser,
        enterpriseEcpdId = '',
        email = '',
        isPwnTracker = false,
        isTrackerComplete = false,
        trackerInstallationType = ''
    } = useSelector((state) => state.auth);
    const { count } = useSelector((state) => state.notifications);
    const [confirmLogout, setConfirmLogout] = useState(false);
    const [supportPopup, setSupportPopup] = useState(false);
    const [showAllLocationsModal, setAllLocationsModal] = useState(false);
    const [showVideoTutorialModal, setShowVideoTutorialModal] = useState(false);
    const [showNewFeatureModal, setShowNewFeatureModal] = useState(false);
    const [isPublished, setIsPublished] = useState(true);
    const [content, setContent] = useState({});
    const [installationType, setInstallationType] = useState('');
    const [notificationPopup, setNotificationPopup] = useState(false);
    const isMultiNibAllowed = userAllowed(userRoles, [roles.MANAGE_MULTI_NIB_COMBINED_VIEW]);
    const selectedLocation = getSelectedLocation();
    const locationOptions = getMultiLocationOptions();
    const isPwnTrackerDisabled = !isPwnTracker || (isTrackerComplete && userAllowed(userRoles, [roles.VIEW_OSND_URL_IN_TRACKER]));
    const isPwnTrackerPage = window.location.pathname === '/pwn-tracker';

    const trackingParameters = {
        enterprise_id: enterpriseEcpdId,
        email
    };

    useEffect(() => {
        fetchInit();
    }, []);

    useEffect(() => {
        if (!isPwnTrackerDisabled && (isPwnTrackerPage || window.location.pathname === '/journey/guide')) {
            setInstallationType(isPwnTracker ? trackerInstallationType : '');
        }
    }, [trackerInstallationType]);

    // Fetch notifications and new feature content in parallel
    const fetchInit = async () => {
        // Removing call till new feature is added
        await fetchNotifications();
    };

    const fetchNotifications = async () => {
        try {
            const response = await dispatcher(getUserNotifications());
            updateNotificationCount(dispatcher, response?.data?.uncheckedNotifications + response?.data?.uncheckedFutureNotifications);
        } catch (err) {
            console.error(err?.message || err);
        }
    };

    const fetchNewFeature = async () => {
        try {
            const response = await dispatcher(getNewFeatureContent({}));
            setContent(response || {});
            setIsPublished(response?.isPublished ?? false);
        } catch (err) {
            console.error(err?.message || err);
        }
    };

    const toggleMobileSubNav = useCallback(() => {
        setShowMobileSubNav(true);
    }, []);

    const onClickGoTo = useCallback((event, { value }) => {
        history.push(value);
    }, []);

    const onSupportClick = () => {
        trackSupportIconClicked(trackingParameters);
        setSupportPopup(true);
    };
    const onCreateTicketClick = () => {
        trackCreateTicketIconClicked(trackingParameters);
        redirectTo(CREATE_TICKET_URL, true);
    };
    const onPendoFeedbackFormClick = () => {
        trackFeedbackFormIconClicked(trackingParameters);
    };
    const onUserGuideClick = () => {
        trackUserGuideMenuItemClicked(trackingParameters);
        redirectTo('/help');
    };
    const onLogoutClick = () => {
        setConfirmLogout(true);
    };
    const logout = () => {
        osndLocalStorage.remove(storageConstant.LINKED_ENTERPRISES);
        redirectTo('/logout');
    };

    const onNotifictionClick = () => {
        setNotificationPopup(!notificationPopup);
    };

    const updateSelectedEnterprise = (enterpriseId) => {
        const linkedEnterprises = osndLocalStorage.getArray(storageConstant.LINKED_ENTERPRISES);
        if (linkedEnterprises?.length > 0) {
            linkedEnterprises.forEach((element) => {
                element.selected = element.enterpriseId === enterpriseId;
            });
            osndLocalStorage.setArray(storageConstant.LINKED_ENTERPRISES, linkedEnterprises);
        }
    };

    const refreshNewLocation = (newLocation) => {
        const linkedEnterprises = osndLocalStorage.getArray(storageConstant.LINKED_ENTERPRISES);
        if (linkedEnterprises?.length > 0) {
            const newEnterpriseId = newLocation === 'all_locations' ? enterpriseId : newLocation;
            // If it's on dashboard, refresh the page, otherwise navigate to dashboard page
            const dashboardPath = `/#${newEnterpriseId}`;
            if (window.location.pathname === '/') {
                window.location.reload();
            } else {
                history.replace(dashboardPath);
            }
        }
    };

    const loadEnterprise = async (enterpriseId) => {
        updateSelectedEnterprise(enterpriseId);
        if (enterpriseId !== 'all_locations') {
            const location = linkedEnterprises.find((el) => el?.enterpriseId === enterpriseId);
            if (location?.enterpriseId) {
                const response = await dispatcher(
                    adminLogin({ id: location?.enterpriseId, name: location?.enterpriseName, ecpdId: location?.ecpdId })
                );
                if (response?.enterpriseId) {
                    refreshNewLocation(enterpriseId);
                }
            }
        } else {
            refreshNewLocation(enterpriseId);
        }
    };

    const onLocationChanged = async (event, { value }) => {
        if (value === 'all_locations') {
            setAllLocationsModal(true);
            return;
        }
        await loadEnterprise(value);
    };

    const onVideoTutorialClick = async () => {
        trackVideoTutorialsMenuItemClicked(trackingParameters);
        setShowVideoTutorialModal(true);
    };

    const onSubmitYes = async () => {
        await loadEnterprise('all_locations');
        setAllLocationsModal(false);
    };

    const handleClose = () => {
        setAllLocationsModal(false);
        setShowVideoTutorialModal(false);
    };

    const onWhatsNewClick = () => {
        setShowNewFeatureModal(true);
    };

    const getLocationOptions = (linkedEnterprises = [], selectedLocation) => {
        const allLocationsCount =
            linkedEnterprises?.filter((enterprise) => !enterprise?.currentStage && enterprise?.solutionType !== SOLUTION_TYPE.UNKNOWN).length - 1 ||
            0;

        // Create all location options with safe meta info
        const locations = linkedEnterprises
            .filter((enterprise) => !(enterprise.enterpriseName === 'All Locations' && isPwnTrackerPage))
            .map((link) => {
                const showTrackerState =
                    link.currentStage && !(link.trackerComplete && userAllowed(link.permissions, [roles.VIEW_OSND_URL_IN_TRACKER]));
                const textPlain = `${link.siteName || link.enterpriseName}${showTrackerState ? ` - ${link.currentStage}` : ''}${
                    link.enterpriseName === 'All Locations' ? ` (${allLocationsCount})` : ''
                }`;

                return {
                    key: link.enterpriseId,
                    value: link.enterpriseId,
                    text: textPlain,
                    content: textPlain,
                    meta: { textPlain, isAll: link.enterpriseName === 'All Locations' }
                };
            });

        const selectedOption = locations.find((l) => l.value === selectedLocation?.enterpriseId);
        // Filter out the selected option so we can build an ordered list
        const filtered = locations.filter((l) => l.value !== selectedLocation?.enterpriseId);

        // Find the All Locations option (if any)
        const allOption = filtered.find((l) => l.meta?.isAll);

        // Build final list:
        // 1) If there is a selected location, show it first (bold)
        // 2) Then show the All Locations option (if it exists and is not the selected one)
        // 3) Then show the remaining locations
        const finalOptions = [];

        if (selectedOption) {
            finalOptions.push({
                ...selectedOption,
                content: <b>{selectedOption.meta.textPlain}</b>
            });

            if (allOption) {
                finalOptions.push(allOption);
            }

            filtered.forEach((opt) => {
                if (!allOption || opt.value !== allOption.value) {
                    finalOptions.push(opt);
                }
            });
        } else {
            // No selected option: try to place All Locations in the 2nd position if present
            if (allOption) {
                // Remove the allOption from filtered list
                const others = filtered.filter((opt) => opt.value !== allOption.value);

                if (others.length === 0) {
                    finalOptions.push(allOption);
                } else {
                    // Keep the original first item, then put All Locations second, then the rest
                    finalOptions.push(others[0]);
                    finalOptions.push(allOption);
                    finalOptions.push(...others.slice(1));
                }
            } else {
                finalOptions.push(...filtered);
            }
        }

        return finalOptions;
    };

    /**
     * Filters and highlights dropdown options based on user query.
     * - Filters options whose text matches query (case-insensitive)
     * - Highlights matched text with <b> tags (for visual emphasis)
     *
     * @param {Array} options - The dropdown options list
     * @param {string} query - The user search query
     * @returns {Array} filtered and highlighted options
     */
    const filterAndHighlightOptions = (options, query) => {
        if (!query || query.trim() === '') return options;
        // Escape user input to safely build a RegExp
        const escapeRegExp = (str) => str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

        const q = query.trim();
        try {
            const regex = new RegExp(escapeRegExp(q), 'i');

            return options
                .filter((opt) => regex.test(opt.meta?.textPlain || opt.text))
                .map((opt) => {
                    const safeText = opt.meta?.textPlain || opt.text;
                    const highlighted = safeText.replace(regex, (match) => `<b>${match}</b>`);
                    return {
                        ...opt,
                        content: <span dangerouslySetInnerHTML={{ __html: highlighted }} />
                    };
                });
        } catch (err) {
            // In case of any unexpected error, return the original options to avoid breaking the UI
            return options;
        }
    };

    return location.pathname !== '/login' && signedIn ? (
        <>
            <Grid.Column textAlign="right" only="computer">
                <Menu text className="header-nav tour-step-1 no-print">
                    {!isPwnTrackerDisabled ? (
                        <Menu.Item className="tracker-menu">
                            <Form>
                                <Form.Group style={{ marginBottom: 0 }}>
                                    <span style={{ fontSize: 16, fontWeight: 600, marginTop: 6 }}>Installation Type: </span>
                                    <span className="installation-type">{installationType}</span>
                                    <span className="seperator"> | </span>
                                </Form.Group>
                            </Form>
                        </Menu.Item>
                    ) : null}
                    {isMultiNibAllowed && !showEnterpriseSelection && locationOptions?.length > 1 ? (
                        <Menu.Item className="location-menu">
                            <Form>
                                <Form.Group style={{ marginBottom: 0 }}>
                                    <span style={{ fontSize: 16, fontWeight: 600, marginTop: 12 }}>Location: </span>
                                    <FormSelect
                                        control={Select}
                                        name="location"
                                        value={selectedLocation?.enterpriseId}
                                        onChange={onLocationChanged}
                                        options={getLocationOptions(linkedEnterprises, selectedLocation)}
                                        search={filterAndHighlightOptions}
                                    />
                                </Form.Group>
                            </Form>
                        </Menu.Item>
                    ) : null}
                    <Menu.Item onClick={onNotifictionClick} disabled={showEnterpriseSelection || isPwnTrackerPage}>
                        <Popup
                            className="notifn-popup"
                            content={<NotificationComponent />}
                            on="click"
                            position="bottom center"
                            style={{
                                textAlign: 'left',
                                border: 'none',
                                borderRadius: '5px',
                                boxShadow: '0 2px 6px -1px #999'
                            }}
                            trigger={
                                <div>
                                    <img src={notification} title="Notifications" />
                                    <span className="notification-badge">{count || 0}</span>
                                </div>
                            }></Popup>
                    </Menu.Item>
                    <Menu.Item onClick={onSupportClick} disabled={isPwnTrackerPage}>
                        <img src={support} title="Support" />
                    </Menu.Item>
                    {ENABLE_PENDO_FEEDBACK ? (
                        <Menu.Item disabled={isPwnTrackerPage} onClick={onPendoFeedbackFormClick} className="feedback-icon-container cursor-pointer">
                            <img src={feedback} width={24} height={24} className="feedback-icon" title="Feedback form"></img>
                        </Menu.Item>
                    ) : (
                        <Menu.Item
                            disabled={isPwnTrackerPage}
                            onClick={() => redirectTo(FEEDBACK_FORM_URL + window.location?.host + window.location?.pathname, true)}>
                            <img src={feedback} width={24} height={24} className="feedback-icon" title="Feedback form"></img>
                        </Menu.Item>
                    )}
                    <Menu.Item onClick={onCreateTicketClick} disabled={showEnterpriseSelection || isMultiSiteUser || isPwnTrackerPage}>
                        <img src={ticket} title="Create a ticket" />
                    </Menu.Item>
                    <Menu.Item disabled={showEnterpriseSelection}>
                        <Dropdown className="add-liner" trigger={<img src={appLauncher} title="Verizon apps" />} icon={null}>
                            <Dropdown.Menu>
                                <Dropdown.Item text="My business" onClick={() => redirectTo(MY_BIZ_URL, true)} />
                                <Dropdown.Item text="5G edge developer" onClick={() => redirectTo(EDGE_DEVELOPER_URL, true)} />
                                <Dropdown.Item text="ThingSpace manage" onClick={() => redirectTo(THINGSPACE_URL, true)} />
                            </Dropdown.Menu>
                        </Dropdown>
                    </Menu.Item>
                    <Menu.Item>
                        <Dropdown
                            className="add-liner"
                            disabled={isPwnTrackerPage}
                            trigger={<img src={help} title="User and video guides" />}
                            icon={null}>
                            <Dropdown.Menu>
                                <Dropdown.Item text="Download PDF guide" onClick={onUserGuideClick} disabled={showEnterpriseSelection} />
                                {ENABLE_VIDEO_GUIDE && <Dropdown.Item text="Video guide" onClick={() => redirectTo('/video/guide')} />}
                                {<Dropdown.Item text="Video tutorials" onClick={onVideoTutorialClick} />}

                                {/* TODO: Uncomment when the feature is fully completed on Q1 */}
                                {/* <Dropdown.Item text="What's new" onClick={onWhatsNewClick} /> */}
                            </Dropdown.Menu>
                        </Dropdown>
                    </Menu.Item>
                    <Menu.Item>
                        <Dropdown trigger={<img src={userProfile} title="Sign out" />} icon={null}>
                            <Dropdown.Menu>
                                <Dropdown.Item text="Logout" onClick={onLogoutClick} />
                            </Dropdown.Menu>
                        </Dropdown>
                    </Menu.Item>
                </Menu>
                {confirmLogout && (
                    <ConfirmMessage
                        open
                        message={
                            <>
                                <h2>Are you sure?</h2>
                                <div>Clicking confirm will logout from OSND and MyBiz.</div>
                            </>
                        }
                        confirmBtnText="Confirm"
                        cancelBtnText="Take me back"
                        handleClose={() => setConfirmLogout(false)}
                        handleConfirm={logout}
                        size="tiny"
                    />
                )}

                {supportPopup && (
                    <ConfirmMessage
                        open
                        message={
                            <>
                                <h2>Support</h2>
                                <hr />
                                <div>Send us an email with your question or issue:</div>
                                <h1>
                                    <a href="mailto:onsitelte@verizon.com" style={{ textDecoration: 'underline', color: 'inherit' }}>
                                        onsitelte@verizon.com
                                    </a>
                                </h1>
                                {/* <hr style={{ borderWidth: '0.5px', borderColor: '#f5f5f5' }} />
                                <div>Or call us 24x7:</div>
                                <h1>800-525-0481</h1>
                                <div>
                                    Select prompt <strong>6</strong>, enter extension <strong>7483</strong> when prompted.
                                </div> */}
                            </>
                        }
                        size="tiny"
                        handleClose={() => setSupportPopup(false)}
                        closeIcon={true}
                        hideActions={true}
                    />
                )}
                {!userAllowed(userRoles, [roles.ADMIN_USER]) && (
                    <TextLink className="portal no-print" value="/portal" onClick={onClickGoTo}>
                        Back to Portal
                    </TextLink>
                )}
            </Grid.Column>
            <Grid.Column textAlign="right" only="mobile tablet">
                <TextLink className="mobile-nav no-print" icon={<Icon className="menu" />} onClick={toggleMobileSubNav} />
            </Grid.Column>
            {showAllLocationsModal && (
                <Modal className="all-locations-modal" open closeIcon size="tiny" onClose={handleClose}>
                    <Header className="all-location-model-header">Are you sure?</Header>
                    <Modal.Content>
                        <Grid>
                            <Grid.Row style={{ display: 'block' }}>
                                Selecting <strong>All Locations</strong> will redirect you to the Dashboard page and you will lose any data you have
                                not saved.
                            </Grid.Row>
                        </Grid>
                    </Modal.Content>
                    <Modal.Actions>
                        <Button content="Yes, show me all locations" onClick={onSubmitYes} />
                        <Button secondary onClick={handleClose}>
                            Cancel
                        </Button>
                    </Modal.Actions>
                </Modal>
            )}

            {showVideoTutorialModal && (
                <Modal className="osnd-video-tutorial" open={open} closeIcon onClose={handleClose}>
                    <Header className="modal-header">OSND video tutorials</Header>
                    <hr />
                    <Modal.Content>
                        <Grid>
                            <Grid.Row>
                                <Grid.Column width={12}>
                                    <div className="main-video" style={{ position: 'relative' }}>
                                        <iframe
                                            id="ytplayer"
                                            type="text/html"
                                            width="870"
                                            height="489.375"
                                            src={OSND_VIDEO_URL}
                                            frameBorder="0"
                                            referrerpolicy="strict-origin-when-cross-origin"
                                            allowFullScreen></iframe>
                                    </div>
                                </Grid.Column>
                            </Grid.Row>
                        </Grid>
                    </Modal.Content>
                </Modal>
            )}

            {(showNewFeatureModal || !isPublished) && (
                <Modal
                    className="new-feature-modal"
                    open={open}
                    closeIcon
                    onClose={() => {
                        setShowNewFeatureModal(false);
                        setIsPublished(true);
                    }}>
                    <Header className="modal-header">What's new</Header>
                    <Modal.Content>
                        <Grid>
                            <Grid.Row>
                                <Grid.Column width={12}>
                                    <div className="content-container" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                        <img src={newFeature} alt={'newFeature'} />
                                        <div className="feature-content">
                                            <p className="content-date">{content.contentDate}</p>
                                            <div dangerouslySetInnerHTML={{ __html: content?.note }} />
                                        </div>
                                    </div>
                                </Grid.Column>
                            </Grid.Row>
                        </Grid>
                    </Modal.Content>
                    <Modal.Actions>
                        <Button
                            secondary
                            onClick={() => {
                                setShowNewFeatureModal(false);
                                setIsPublished(true);
                            }}>
                            Close
                        </Button>
                    </Modal.Actions>
                </Modal>
            )}
        </>
    ) : null;
};

export default PageNav;
